import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import I10n from '../../features/i10n';
import Question from '../question';
import { powerSupplyVoltageCompatibilityCriteria, questionHintTypes } from '../../constants';
import LoadingSpinner from '../../atoms/loading';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  color: #626469;
  padding-top: 10px;
`;

const TitleRow = styled.div`
  display: flex;
  font-size: 24px;
  align-items: center;
  margin-bottom: 10px;
`;

const Title = styled.span`
  font-weight: bold;
`;

const QuestionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  margin-top: 10px;
  padding-right: 10px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
`;

class RequirementsStep extends React.Component {

  static propTypes = {
    questions: PropTypes.array,
    questionMap: PropTypes.object,
    getPowerSuppliesForRequirements: PropTypes.func,
    questionsWithAnswers: PropTypes.object,
    questionsLoading: PropTypes.bool,
    resultsLoading: PropTypes.bool,
    getPowerSupplyQuestions: PropTypes.func,
    onAnswerChanged: PropTypes.func,
    plcVoltage: PropTypes.string,
    wrapperElement: PropTypes.object
  }

  constructor(props) {
    super(props);
    this._isMounted = false;
  }

  componentDidMount() {
    this._isMounted = true;
    this.props.getPowerSupplyQuestions().then(() => {
      this.props.getPowerSuppliesForRequirements();
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  updateStateIfMounted(objectToAffect) {
    if (this._isMounted) {
      this.setState(objectToAffect);
    }
  }

  buildCriteriaPayload(questionsWithAnswers) {
    const payload = {};

    // Add the criteria for power supply output voltage
    payload[powerSupplyVoltageCompatibilityCriteria] = [this.props.plcVoltage];


    for (const [key, answer] of Object.entries(questionsWithAnswers)) {
      try {
        payload[this.props.questionMap[key].criteria] = answer;
      } catch (error) {
        // Filter non existing criteria
      }
    }
    return payload;
  }

  /**
   * Removes all questions with answer which depends of the question that was updated.
   * @param {*} questionKey The question key of the question where answer was updated
   */
  removeAnswerFromQuestionDependingFromQuestionKey(questionKey) {
    const newQuestionsWithAnswer = this.props.questionsWithAnswers;
    const keysOfQuestionsToRemove = [questionKey];
    // Remove all the dependency tree with a stack
    while (keysOfQuestionsToRemove.length > 0) {
      const currentKey = keysOfQuestionsToRemove.pop();
      // Check if there is a question which depends from the current question key.
      for (const key of Object.keys(this.props.questionsWithAnswers)) {
        const question = this.props.questionMap[key];
        // If found, push it and delete the answer to delete questions which also depends
        // from this one.
        if (question?.previousQuestionKey === currentKey) {
          keysOfQuestionsToRemove.push(question.questionKey);
          delete newQuestionsWithAnswer[question.questionKey];
        }
      }
    }
    return newQuestionsWithAnswer;
  }

  clearEmptyAnswer = () => {
    const newQuestionsWithAnswer = this.props.questionsWithAnswers;
    const keys = Object.keys(newQuestionsWithAnswer);
    for (let i = 0; i < keys.length; i++) {
      if (newQuestionsWithAnswer[keys[i]].length === 0) {
        delete newQuestionsWithAnswer[keys[i]];
      }
    }

    return newQuestionsWithAnswer;
  }

  onAnswerChanged = (question, value, status) => {
    let newQuestionsWithAnswer = this.props.questionsWithAnswers;
    if (status === 'remove') {
      newQuestionsWithAnswer[question.questionKey] = newQuestionsWithAnswer[question.questionKey].filter(e => e !== value);
      newQuestionsWithAnswer = this.clearEmptyAnswer();
    } else {
      if (newQuestionsWithAnswer[question.questionKey]) {
        newQuestionsWithAnswer[question.questionKey].push(value);
      } else {
        newQuestionsWithAnswer[question.questionKey] = [value];
      }
    }

    const newRequirements = this.removeAnswerFromQuestionDependingFromQuestionKey(question.questionKey)

    this.props.getPowerSuppliesForRequirements(this.buildCriteriaPayload(newRequirements));

    this.props.onAnswerChanged
      (newRequirements);
  }

  renderQuestions = (questions) => {
    return questions.map(question => {
      return (
        <Question
          key={question.questionKey}
          question={question}
          onAnswerChanged={this.onAnswerChanged}
          answerGiven={
            this.props.questionsWithAnswers[question.questionKey] ? this.props.questionsWithAnswers[question.questionKey] : null
          }
          remainingOptions={this.props.remainingOptions}
          sharedOptions={this.props.sharedOptions}
          baseOptions={this.props.baseOptions}
          resultsLoading={this.props.resultsLoading}
          scrollbarWrapper={this.props.wrapperElement}
          showOptional={true}
          questionHintType={questionHintTypes.dropdown}
        >
        </Question>
      );
    });
  }

  _getPreviousQuestionsAnswers = (question) => {
    const questionsWithAnswers = this.props.questionsWithAnswers;
    if (questionsWithAnswers[question?.previousQuestionKey]) {
      return questionsWithAnswers[question?.previousQuestionKey];
    }
    return null;
  }

  _getPreviousSharedQuestionAnswers = (question) => {
    const sharedOptions = this.props.sharedOptions;

    if (this.props.questionMap[question?.previousQuestionKey] && sharedOptions[this.props.questionMap[question?.previousQuestionKey].criteria]) {
      return sharedOptions[this.props.questionMap[question?.previousQuestionKey].criteria];
    }
    return null;
  }

  _questionHasOptions = (question) => {
    if (this.props.remainingOptions
      && this.props.remainingOptions[question.criteria]) {
      const remainingOptions = this.props.remainingOptions[question.criteria];
      const questionOptions = question.options.map(q => q.criteriaValue);
      const intersection = remainingOptions.filter(value => questionOptions.includes(value));
      return intersection.length > 0;
    }
    return true;
  }

  _questionShouldBeActive = (question) => {
    const havePreviousTrigger = !!question?.previousQuestionKey && question.previousQuestionsTrigger?.length > 0;
    if (!havePreviousTrigger) {
      return true;
    }
    const previousQuestionsAnswers = this._getPreviousQuestionsAnswers(question) || [];
    const previousQuestionsTrigger = question.previousQuestionsTrigger || [];
    const answersAndTriggerIntersection = previousQuestionsTrigger.filter(value => previousQuestionsAnswers.includes(value));

    let shouldBeActive = answersAndTriggerIntersection.length > 0;
    if (this.props.sharedOptions) {
      const previousSharedOptions = this._getPreviousSharedQuestionAnswers(question) || [];
      const sharedAndTriggerIntersection = previousQuestionsTrigger.filter(value => previousSharedOptions.includes(value));
      shouldBeActive = shouldBeActive || sharedAndTriggerIntersection.length > 0;
    }

    return shouldBeActive;
  }

  render = () => {
    const enabledQuestions = this.props.questions
      .filter(question => this._questionShouldBeActive(question) && this._questionHasOptions(question));
    return (
      <Container>
        <TitleRow>
          <Title>{I10n.get('screen.power_supply_screen.requirements_step.title')}</Title>
        </TitleRow>
        <QuestionsWrapper>
          {this.props.questionsLoading ? <LoadingWrapper><LoadingSpinner></LoadingSpinner></LoadingWrapper> : this.renderQuestions(enabledQuestions)}
        </QuestionsWrapper>

      </Container>
    );
  }
}

export default RequirementsStep;
